import { type FC, useRef } from "react";
import { ArrowRight } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from "@/shared/components/ui/dialog";

import type { DeceasedUserData } from "@/shared/hooks/useDeceasedUserData";
import type { EssentialModule, FormField } from "@/shared/types/estateEssentials";
import type { EstateItem } from "@/shared/types/estateTypes";

import {
  extractAddressFieldInfo,
  groupFieldsBySectionGroup,
  resolveTemplate,
} from "../utils/formDisplayUtils";
import { resolveFieldOptions, isFieldHidden } from "../utils/resolveFieldOptions";
import { useGuideForm } from "../hooks/useGuideForm";
import { AddressBlock } from "./AddressBlock";
import { FieldGrid, FieldLabel } from "./FieldGrid";
import { FormFieldInput } from "./FormFieldInput";
import { ReviewSection } from "./ReviewSection";
import { SectionNumber, AttentionBanner } from "./SectionHeader";
import { VehicleCountSection, VehicleTransferSection } from "./VehicleSections";

// Re-export for ReviewSection backward compat
export { SectionNumber } from "./SectionHeader";

interface GuideFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  module: EssentialModule;
  initialData?: Record<string, string>;
  onSubmit: (formData: Record<string, string>) => void;
  onBack?: () => void;
  deceasedData?: DeceasedUserData;
  relationship?: string;
  vehicleItems?: EstateItem[];
}

interface FormDialogShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBack?: () => void;
  title: string;
  submitText: React.ReactNode;
  onSubmit: () => void;
  errors: Record<string, string>;
  dialogRef: React.RefObject<HTMLDivElement | null>;
  size?: "xl";
  children: React.ReactNode;
}

const FormDialogShell: FC<FormDialogShellProps> = ({
  open,
  onOpenChange,
  title,
  submitText,
  onSubmit,
  errors,
  dialogRef,
  size,
  children,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent
      ref={dialogRef}
      size={size}
      className="min-h-0"
      onPointerDownOutside={(e) => e.preventDefault()}
      onEscapeKeyDown={(e) => e.preventDefault()}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogBody className="space-y-6">{children}</DialogBody>
      <DialogFooter onSubmit={onSubmit} submitText={submitText} rounded fullWidth>
        {Object.keys(errors).length > 0 && (
          <p className="text-sm text-amber-600">Please fill in all required fields.</p>
        )}
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const GuideFormModal: FC<GuideFormModalProps> = ({
  open,
  onOpenChange,
  module,
  initialData,
  onSubmit,
  onBack,
  deceasedData,
  relationship,
  vehicleItems = [],
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const hasVehicleItems = vehicleItems.length > 0;

  const form = useGuideForm({
    open,
    module,
    initialData,
    onSubmit,
    deceasedData,
    relationship,
    vehicleItems,
  });

  // Empty fields: simple save dialog
  if (form.fields.length === 0) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="min-h-0">
          <div>
            <h4>Save</h4>
            <p className="text-sm text-secondary">
              No additional information is needed for this module.
            </p>
          </div>
          <Button variant="brand" rounded className="w-full" onClick={() => onSubmit({})}>
            Save and close
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  // Custom sections layout (vehicles, utilities, etc.)
  const customSections = module.formSections;
  if (customSections && customSections.length > 0) {
    return (
      <CustomSectionsLayout
        open={open}
        onOpenChange={onOpenChange}
        onBack={onBack}
        module={module}
        customSections={customSections}
        form={form}
        vehicleItems={vehicleItems}
        hasVehicleItems={hasVehicleItems}
        relationship={relationship}
        dialogRef={dialogRef}
      />
    );
  }

  // Two-section layout (review + editable)
  if (form.hasSections) {
    return (
      <TwoSectionLayout
        open={open}
        onOpenChange={onOpenChange}
        onBack={onBack}
        title={module.formTitle}
        form={form}
        relationship={relationship}
        dialogRef={dialogRef}
      />
    );
  }

  // Simple form layout
  return (
    <SimpleFormLayout
      open={open}
      onOpenChange={onOpenChange}
      module={module}
      form={form}
      dialogRef={dialogRef}
    />
  );
};

// --- Custom Sections Layout ---

interface CustomSectionsLayoutProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBack?: () => void;
  module: EssentialModule;
  customSections: NonNullable<EssentialModule["formSections"]>;
  form: ReturnType<typeof useGuideForm>;
  vehicleItems: EstateItem[];
  hasVehicleItems: boolean;
  relationship?: string;
  dialogRef: React.RefObject<HTMLDivElement | null>;
}

const CustomSectionsLayout: FC<CustomSectionsLayoutProps> = ({
  open,
  onOpenChange,
  onBack,
  module,
  customSections,
  form,
  vehicleItems,
  hasVehicleItems,
  relationship,
  dialogRef,
}) => {
  const isVehicleModule = module.id === "vehicle-boat-titles";
  const sectionFieldsMap = groupFieldsBySectionGroup(form.fields, customSections);
  const attentionSectionNumbers = computeAttentionSections(
    customSections,
    sectionFieldsMap,
    form,
    isVehicleModule,
    hasVehicleItems,
    vehicleItems,
  );

  return (
    <FormDialogShell
      open={open}
      onOpenChange={onOpenChange}
      onBack={onBack}
      title={isVehicleModule ? "Let's personalize this guide" : "Let's customize your guide"}
      submitText={
        isVehicleModule ? (
          <>
            <span>Customize my guide</span>
            <ArrowRight className="h-4 w-4" />
          </>
        ) : (
          <>
            <span>Update my guide</span>
            <ArrowRight className="h-4 w-4" />
          </>
        )
      }
      onSubmit={form.handleSubmit}
      errors={form.errors}
      dialogRef={dialogRef}
      size="xl"
    >
      {attentionSectionNumbers.length > 0 && (
        <AttentionBanner
          count={form.activeAttentionCount}
          sectionNumbers={attentionSectionNumbers}
        />
      )}

      {customSections.map((section, idx) => {
        const sectionFields = sectionFieldsMap.get(section.key) ?? [];
        if (sectionFields.length === 0) return null;

        const sectionLabel = resolveTemplate(section.label, form.deceasedFirstName);

        if (isVehicleModule && hasVehicleItems && section.key === "vehicle_count") {
          return (
            <VehicleCountSection
              key={section.key}
              sectionIndex={idx}
              sectionLabel={sectionLabel}
              sectionFields={sectionFields}
              vehicleItems={vehicleItems}
              formValues={form.formValues}
              setFormValues={form.setFormValues}
            />
          );
        }

        if (isVehicleModule && section.key === "transfer_info") {
          return (
            <VehicleTransferSection
              key={`transfer_info_${form.formValues.selected_vehicle_id ?? "default"}`}
              sectionIndex={idx}
              sectionFields={sectionFields}
              vehicleItems={vehicleItems}
              hasVehicleItems={hasVehicleItems}
              formValues={form.formValues}
              errors={form.errors}
              onFieldChange={form.handleFieldChange}
              attentionKeys={form.attentionKeys}
              relationship={relationship}
              deceasedFirstName={form.deceasedFirstName}
              portalContainer={dialogRef.current}
            />
          );
        }

        const {
          addressField: sectionAddrField,
          addrPrefix: addrPfx,
          nonAddressFields: nonAddrFields,
        } = extractAddressFieldInfo(sectionFields);
        const resolvedNonAddrFields = nonAddrFields
          .filter((f) => !isFieldHidden(f, form.formValues))
          .map((f) => resolveFieldOptions(f, form.formValues));

        if (section.mode === "review" && form.isReviewMode) {
          return (
            <div key={section.key}>
              {idx > 0 && <hr className="border-primary mb-6" />}
              <ReviewSection
                sectionNumber={idx + 1}
                sectionTitle={sectionLabel}
                fields={sectionFields}
                values={form.formValues}
                onFieldChange={form.handleFieldChange}
                errors={form.errors}
                deceasedFirstName={form.deceasedFirstName}
              />
            </div>
          );
        }

        return (
          <div key={section.key}>
            {idx > 0 && <hr className="border-primary mb-6" />}
            <div className="space-y-3">
              <p className="flex items-center gap-2 text-sm font-medium">
                <SectionNumber n={idx + 1} />
                {sectionLabel}
              </p>
              {resolvedNonAddrFields.length > 0 && (
                <FieldGrid
                  fields={resolvedNonAddrFields}
                  values={form.formValues}
                  errors={form.errors}
                  onFieldChange={form.handleFieldChange}
                  relationship={relationship}
                  attentionKeys={form.attentionKeys}
                  deceasedFirstName={form.deceasedFirstName}
                  portalContainer={dialogRef.current}
                />
              )}
              {sectionAddrField && (
                <AddressBlock
                  prefix={addrPfx}
                  values={form.formValues}
                  onFieldChange={form.handleFieldChange}
                  dialogRef={dialogRef}
                />
              )}
            </div>
          </div>
        );
      })}
    </FormDialogShell>
  );
};

function computeAttentionSections(
  customSections: NonNullable<EssentialModule["formSections"]>,
  sectionFieldsMap: Map<string, FormField[]>,
  form: ReturnType<typeof useGuideForm>,
  isVehicleModule: boolean,
  hasVehicleItems: boolean,
  vehicleItems: EstateItem[],
): number[] {
  const result: number[] = [];
  const multiVehicleNoSelection =
    isVehicleModule && vehicleItems.length > 1 && !form.formValues.selected_vehicle_id;

  for (let i = 0; i < customSections.length; i++) {
    const sectionKey = customSections[i].key;

    if (isVehicleModule && sectionKey === "vehicle_count" && multiVehicleNoSelection) {
      result.push(i + 1);
      continue;
    }

    if (isVehicleModule && sectionKey === "transfer_info" && multiVehicleNoSelection) continue;

    const sFields = sectionFieldsMap.get(sectionKey) ?? [];

    if (isVehicleModule && !hasVehicleItems && sectionKey === "transfer_info") {
      const count = Math.max(1, parseInt(form.formValues.vehicle_count ?? "1", 10));
      if (count > 1) {
        for (let vi = 0; vi < count; vi++) {
          const prefix = vi === 0 ? "" : `v${vi}_`;
          const hasEmpty = sFields.some(
            (f) => f.required && !form.formValues[`${prefix}${f.key}`]?.trim(),
          );
          if (hasEmpty) result.push(i + 1 + vi);
        }
        continue;
      }
    }

    const hasEmptyRequired = sFields.some(
      (f) =>
        !isFieldHidden(f, form.formValues) &&
        f.required &&
        form.attentionKeys.has(f.key) &&
        !form.formValues[f.key]?.trim(),
    );
    if (hasEmptyRequired) result.push(i + 1);
  }

  return result;
}

// --- Two-Section Layout ---

interface TwoSectionLayoutProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBack?: () => void;
  title?: string;
  form: ReturnType<typeof useGuideForm>;
  relationship?: string;
  dialogRef: React.RefObject<HTMLDivElement | null>;
}

const TwoSectionLayout: FC<TwoSectionLayoutProps> = ({
  open,
  onOpenChange,
  onBack,
  title,
  form,
  relationship,
  dialogRef,
}) => {
  if (form.isReviewMode) {
    return (
      <FormDialogShell
        open={open}
        onOpenChange={onOpenChange}
        onBack={onBack}
        title="Review the details below"
        submitText={
          <>
            <span>Update my guide</span>
            <ArrowRight className="h-4 w-4" />
          </>
        }
        onSubmit={form.handleSubmit}
        errors={form.errors}
        dialogRef={dialogRef}
        size="xl"
      >
        <ReviewSection
          sectionNumber={1}
          sectionTitle={`${form.deceasedFirstName}'s information`}
          fields={form.reviewFields}
          values={form.formValues}
          onFieldChange={form.handleFieldChange}
          errors={form.errors}
          deceasedFirstName={form.deceasedFirstName}
        />

        <hr className="border-primary" />

        <ReviewSection
          sectionNumber={2}
          sectionTitle="Your information"
          fields={form.editableFields}
          values={form.formValues}
          onFieldChange={form.handleFieldChange}
          errors={form.errors}
          deceasedFirstName={form.deceasedFirstName}
        />
      </FormDialogShell>
    );
  }

  return (
    <FormDialogShell
      open={open}
      onOpenChange={onOpenChange}
      onBack={onBack}
      title={title || "Let's personalize this guide"}
      submitText={
        <>
          <span>Update my guide</span>
          <ArrowRight className="h-4 w-4" />
        </>
      }
      onSubmit={form.handleSubmit}
      errors={form.errors}
      dialogRef={dialogRef}
      size="xl"
    >
      {form.activeAttentionCount > 0 && <AttentionBanner count={form.activeAttentionCount} />}

      <div className="space-y-3">
        <p className="flex items-center gap-2 text-sm font-medium">
          <SectionNumber n={1} />
          {`${form.deceasedFirstName}'s information`}
        </p>
        <FieldGrid
          fields={form.nonAddressReviewFields}
          values={form.formValues}
          errors={form.errors}
          onFieldChange={form.handleFieldChange}
          attentionKeys={form.attentionKeys}
          deceasedFirstName={form.deceasedFirstName}
          portalContainer={dialogRef.current}
        />
        {form.hasReviewAddressField && (
          <AddressBlock
            prefix={form.reviewAddrPrefix}
            values={form.formValues}
            onFieldChange={form.handleFieldChange}
            dialogRef={dialogRef}
          />
        )}
      </div>

      <hr className="border-primary" />

      <div className="space-y-3">
        <p className="flex items-center gap-2 text-sm font-medium">
          <SectionNumber n={2} />
          Your information
        </p>

        {form.nonAddressEditableFields.length > 0 && (
          <FieldGrid
            fields={form.nonAddressEditableFields}
            values={form.formValues}
            errors={form.errors}
            onFieldChange={form.handleFieldChange}
            relationship={relationship}
            attentionKeys={form.attentionKeys}
            deceasedFirstName={form.deceasedFirstName}
            portalContainer={dialogRef.current}
          />
        )}

        {form.hasAddressField && (
          <AddressBlock
            prefix={form.addrPrefix}
            values={form.formValues}
            onFieldChange={form.handleFieldChange}
            dialogRef={dialogRef}
          />
        )}
      </div>
    </FormDialogShell>
  );
};

// --- Simple Form Layout ---

interface SimpleFormLayoutProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  module: EssentialModule;
  form: ReturnType<typeof useGuideForm>;
  dialogRef: React.RefObject<HTMLDivElement | null>;
}

const SimpleFormLayout: FC<SimpleFormLayoutProps> = ({
  open,
  onOpenChange,
  module,
  form,
  dialogRef,
}) => (
  <FormDialogShell
    open={open}
    onOpenChange={onOpenChange}
    title={module.name}
    submitText="Save and close"
    onSubmit={form.handleSubmit}
    errors={form.errors}
    dialogRef={dialogRef}
  >
    <p className="text-sm text-secondary -mt-4">
      Fill in the details below to generate your personalized guide.
    </p>
    {form.fields.map((field) => (
      <div key={field.key} className="space-y-1.5">
        <FieldLabel field={field} />
        <FormFieldInput
          field={field}
          value={form.formValues[field.key] ?? ""}
          onFieldChange={form.handleFieldChange}
          fieldKey={field.key}
          error={!!form.errors[field.key]}
        />
        <FieldError message={form.errors[field.key]} />
      </div>
    ))}
  </FormDialogShell>
);
